import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as Sentry from '@sentry/react';
import useToast from 'src/hooks/useToast';
import { Checkbox } from '@Thread-Magic/jasmine';
import SimpleButton from 'src/components/SimpleButton';
import Alert from 'src/components/Alert';
import { psaTypes, languageOptions } from 'src/constants';
import queryKeys from 'src/constants/queryKeys';
import { chatgenieAPI } from 'src/config/api';
import { useMutation, useQuery } from 'react-query';
import { queryClient } from 'src/config/queryClient';
import { transformToCamelCase } from 'src/utils/data-transformer';
import { psaTranslate } from 'src/utils/psa';
import CustomSelect from './CustomSelect';
import SupportTypeSection from './SupportTypeSection';
import * as Styled from './style';
import MessengerWidget from './MessengerWidget';
import CustomMultiSelect from './CustomMultiSelect';
import Switcher from '../Switcher';

const paginatedSelectStyles = {
  control: state => ({
    background: state.isDisabled ? state.theme.colors.gray3 : state.background
  })
};

const MessengerSettings = ({ companyId, appId, parentCompanyId, psaType }) => {
  const { toast } = useToast();
  const [data, setData] = useState();
  const languageValueOption = data?.lang
    ? languageOptions.find(item => item.value === data?.lang)
    : languageOptions[0];
  const isParentCompany = () => parentCompanyId === companyId;
  const {
    data: messengerSettings,
    isLoading,
    error: parentCompanyError
  } = useQuery([queryKeys.GET_MESSENGER_SETTINGS, parentCompanyId], () =>
    chatgenieAPI.getMessengerSettings()
  );
  const {
    data: companyMessengerSettings,
    isLoading: isLoadingCompanyMessengerSettings,
    error: clientCompanyError
  } = useQuery(
    [queryKeys.GET_MESSENGER_SETTINGS, companyId],
    () => chatgenieAPI.getCompanyMessengerSettings(companyId),
    { enabled: !isParentCompany() }
  );

  const isLoadingAll = isLoading || isLoadingCompanyMessengerSettings || !data;
  const isActionButtonDisabled = isParentCompany()
    ? !data
    : !companyMessengerSettings?.id && !data?.overrideSettings;

  // We do not support Kaseya anymore, should we enabled this for all PSA types or remove?
  const isDefaultStatusVisible = psaType === psaTypes.KASEYA_BMS && isParentCompany();
  const isDefaultPriorityVisible = [psaTypes.AUTOTASK, psaTypes.HALO].includes(psaType);
  const isDefaultTicketTypeVisible = [psaTypes.HALO].includes(psaType);
  const isDefaultServiceSourceVisible = [psaTypes.CONNECTWISE, psaTypes.AUTOTASK].includes(psaType);
  const isContactTypeVisible = psaType === psaTypes.CONNECTWISE;
  const isHaloPsaType = [psaTypes.HALO].includes(psaType);

  const mutateSettings = useMutation(
    data => {
      if (isParentCompany()) {
        return chatgenieAPI.updateMessengerSettings(data);
      }
      if (companyMessengerSettings?.id) {
        return chatgenieAPI.updateCompanyMessengerSettings({ data, companyId });
      }
      return chatgenieAPI.createCompanyMessengerSettings({ data, companyId });
    },
    {
      onSuccess: res => {
        queryClient.setQueryData(
          [queryKeys.GET_MESSENGER_SETTINGS, isParentCompany() ? parentCompanyId : companyId],
          res
        );
        toast.success('Successfully saved');
      },
      onError: error => {
        let errorMessage = error.response?.data?.message || error.message;

        if (error.response?.data?.errors) {
          errorMessage = Object.values(error.response?.data?.errors).reduce(
            (acc, error) => `${acc} ${error.join(' ')}`,
            `${errorMessage} `
          );
        }

        toast.error(errorMessage);
      }
    }
  );

  const validateData = data => {
    if (!data.supportBoard) {
      toast.warn('Please select Support board');
      return false;
    }
    if (data.salesFlowEnabled && !data.salesBoard && isParentCompany()) {
      toast.warn('Please select Sales board');
      return false;
    }
    if (isDefaultServiceSourceVisible && !data.defaultServiceSource) {
      toast.warn('Please select Chat source');
      return false;
    }
    if (isDefaultPriorityVisible && !data.defaultPriority) {
      toast.warn('Please select Default priority');
      return false;
    }
    if (isDefaultStatusVisible && !data.defaultStatus) {
      toast.warn('Please select Default status');
      return false;
    }
    if (isDefaultTicketTypeVisible && !data.defaultTicketType) {
      toast.warn('Please select Ticket type');
      return false;
    }
    return true;
  };

  const getBoards = (query, page) =>
    chatgenieAPI.getBoards({ query, page, normalizer: res => res.data });
  const getContactTypes = (query, page) =>
    chatgenieAPI.getContactTypes({ query, page, normalizer: res => res.data });
  const getStatuses = (query, page) =>
    chatgenieAPI.getStatuses({
      query,
      page,
      boardId: data.supportBoard?.id,
      normalizer: res => res.data
    });

  const isMessengerConfigured = Boolean(
    !data?.supportBoard || (data?.salesFlowEnabled && !data?.salesBoard)
  );

  const onFormSubmit = event => {
    event.preventDefault();
    const isValid = validateData(data);
    if (!isValid) {
      return;
    }
    const normalizedData = {
      override_settings: data.overrideSettings,
      support_type: data.supportType,
      support_board_id: data.supportBoard?.id,
      contact_type_id: data.contactType?.id,
      ms_teams_error_status_id: data.msTeamsErrorStatus?.id || null,
      allow_new_contacts: data.allowNewContacts,
      proactive_messaging_status: data.proactiveMessagingStatus,
      messenger_excluded_boards: data.messengerExcludedBoards.map(board => board.id),
      lang: data.lang,
      override_bot_prompt_messages: data.overrideBotPromptMessages,
      bot_prompt_message_translations: data.botPromptMessageTranslations[data.lang],
      ...(isDefaultServiceSourceVisible && {
        default_service_source_id: data.defaultServiceSource?.id || null
      }),
      ...(isDefaultTicketTypeVisible && {
        default_ticket_type_id: data.defaultTicketType?.id || null
      }),
      ...(isDefaultPriorityVisible && {
        default_priority_id: data.defaultPriority?.id || null
      }),
      ...(isDefaultStatusVisible && {
        default_status_id: data.defaultStatus?.id || null
      }),
      ...(isParentCompany() && {
        sales_flow_enabled: data.salesFlowEnabled,
        sales_board_id: data.salesBoard?.id,
        email_service_source_id: data.emailServiceSource?.id,
        phone_service_source_id: data.phoneServiceSource?.id
      })
    };
    mutateSettings.mutate(normalizedData);
  };

  useEffect(() => {
    if (messengerSettings || companyMessengerSettings) {
      const settingsData = isParentCompany()
        ? messengerSettings
        : companyMessengerSettings?.id
        ? companyMessengerSettings
        : messengerSettings;

      const normalizedSettingsData = settingsData && {
        ...transformToCamelCase(settingsData),
        overrideSettings: companyMessengerSettings?.override_settings
      };
      setData(normalizedSettingsData);
    }
  }, [messengerSettings, companyMessengerSettings]);

  useEffect(() => {
    if (parentCompanyError || clientCompanyError) {
      const parentCompanyErrorMessage =
        parentCompanyError?.response?.data?.message || parentCompanyError?.message;
      const clientCompanyErrorMessage =
        clientCompanyError?.response?.data?.message || clientCompanyError?.message;
      const error = new Error(
        isParentCompany() ? parentCompanyErrorMessage : clientCompanyErrorMessage
      );
      Sentry.captureException(error);
      toast.error(error.message, {
        id: 'messenger-settings-fetch-error'
      });
    }
  }, [parentCompanyError, clientCompanyError]);

  return (
    <Styled.LocalPageHolder isLoading={isLoadingAll}>
      <Styled.Content>
        {!isLoadingAll && (
          <Styled.Body>
            <Styled.Form
              id="save-settings"
              onSubmit={onFormSubmit}
              disabled={!isParentCompany() && !data.overrideSettings}
            >
              {!isParentCompany() && (
                <>
                  <Styled.FormGroup>
                    <Checkbox
                      className="override-design"
                      id="override-design"
                      checked={Boolean(data.overrideSettings)}
                      onChange={() =>
                        setData(old => ({ ...old, overrideSettings: old.overrideSettings ? 0 : 1 }))
                      }
                    >
                      Override parent settings
                    </Checkbox>
                  </Styled.FormGroup>
                  <hr />
                </>
              )}
              {isMessengerConfigured && (
                <Alert type="warning">
                  Select {psaTranslate('board')}s and save your configuration to start using
                  Messenger.
                </Alert>
              )}
              <Styled.Title marginTop={10}>Messenger settings</Styled.Title>
              <Styled.FormGroup maxWidth="800px">
                <Styled.SubTitle>Support style</Styled.SubTitle>
                <Styled.Description>
                  Set response expectations that match your organization&apos;s goals.
                </Styled.Description>
                <SupportTypeSection
                  value={data.supportType}
                  onChange={event => {
                    const value = event.target.id;
                    setData(old => ({ ...old, supportType: value }));
                  }}
                />
              </Styled.FormGroup>
              <Styled.FormGroup>
                <CustomSelect
                  name="support-board"
                  value={data.supportBoard}
                  options={getBoards}
                  label={`Set default ${psaTranslate('Board')} for chat`}
                  description={`Select the default ${psaTranslate(
                    'Board'
                  ).toLowerCase()} set for new chat threads.`}
                  onChange={option => {
                    setData(old => ({
                      ...old,
                      supportBoard: option,
                      defaultStatus: null,
                      msTeamsErrorStatus: null
                    }));
                  }}
                  componentType="paginate"
                />
              </Styled.FormGroup>
              <Styled.FormGroup>
                <CustomSelect
                  key={data.supportBoard?.id}
                  name="ms_teams_error_status"
                  value={data.msTeamsErrorStatus}
                  isDisabled={!data.supportBoard}
                  options={getStatuses}
                  customStyles={paginatedSelectStyles}
                  onChange={option =>
                    setData(old => ({
                      ...old,
                      msTeamsErrorStatus: option
                    }))
                  }
                  label="Handle chats with errors"
                  description="Select the default status for new threads with Messenger errors."
                  componentType="paginate"
                />
              </Styled.FormGroup>
              {isContactTypeVisible && (
                <Styled.FormGroup>
                  <CustomSelect
                    name="contact-type"
                    value={data.contactType}
                    options={getContactTypes}
                    onChange={option => {
                      setData(old => ({
                        ...old,
                        contactType: option
                      }));
                    }}
                    label="Contact Type"
                    componentType="paginate"
                    description="Select which contact type should be used when new contact is created in PSA."
                  />
                </Styled.FormGroup>
              )}
              {isParentCompany() && (
                <>
                  <Styled.FormGroup oneLine>
                    <div>
                      <Styled.SubTitle>Enable chats from sales leads</Styled.SubTitle>
                      <Styled.Description>
                        Capture new sales leads from your website and let new contacts create
                        threads. Disabling this limits chat to existing contacts for service
                        requests.
                      </Styled.Description>
                    </div>
                    <Switcher
                      className="proactive-messages"
                      id="is-active-flow"
                      checked={!!data.salesFlowEnabled}
                      onChange={() =>
                        setData(old => ({
                          ...old,
                          salesFlowEnabled: old.salesFlowEnabled ? 0 : 1
                        }))
                      }
                      controlText={{ checked: 'on', unchecked: 'off' }}
                    />
                  </Styled.FormGroup>
                  {!!data.salesFlowEnabled && (
                    <Styled.FormGroup>
                      <CustomSelect
                        description={`Select the default ${psaTranslate(
                          'Board'
                        ).toLowerCase()} for these threads.`}
                        name="sales-board"
                        value={data.salesBoard}
                        options={getBoards}
                        placeholder="Select a board for your website leads"
                        onChange={option =>
                          setData(old => ({
                            ...old,
                            salesBoard: option
                          }))
                        }
                        componentType="paginate"
                      />
                    </Styled.FormGroup>
                  )}
                </>
              )}
              <Styled.FormGroup oneLine>
                <div>
                  <Styled.SubTitle>
                    Show all tickets in Chat (formerly &quot;Proactive Messages&quot;)
                  </Styled.SubTitle>
                  <Styled.Description sub>
                    Allow contacts to access all their tickets in Thread Chat, including those
                    submitted by email and phone. If disabled, chat users can only view tickets
                    created through chat. We recommend enabling this setting.
                  </Styled.Description>
                </div>
                <Switcher
                  className="proactive-messages"
                  id="proactive-messages"
                  checked={data.proactiveMessagingStatus === 'enabled'}
                  controlText={{ checked: 'ON', unchecked: 'OFF' }}
                  onChange={() =>
                    setData(old => ({
                      ...old,
                      proactiveMessagingStatus:
                        old.proactiveMessagingStatus === 'enabled' ? 'disabled' : 'enabled'
                    }))
                  }
                />
              </Styled.FormGroup>
              {data.proactiveMessagingStatus === 'enabled' && (
                <Styled.FormGroup>
                  <CustomMultiSelect
                    label={
                      psaType === 'datto_autotask'
                        ? 'Exclude specific queues'
                        : isHaloPsaType
                        ? 'Exclude specific teams'
                        : 'Exclude specific boards'
                    }
                    name="support-board"
                    value={data.messengerExcludedBoards}
                    options={getBoards}
                    onChange={option => {
                      setData(old => ({
                        ...old,
                        messengerExcludedBoards: option
                      }));
                    }}
                    componentType="paginate"
                  />
                </Styled.FormGroup>
              )}

              {!isParentCompany() && (
                <>
                  <Styled.SubTitle>New contacts</Styled.SubTitle>
                  <Styled.FormGroup>
                    <Checkbox
                      className="allow-contacts"
                      id="allow-contacts"
                      checked={Boolean(data.allowNewContacts)}
                      onChange={() =>
                        setData(old => ({ ...old, allowNewContacts: old.allowNewContacts ? 0 : 1 }))
                      }
                    >
                      Allow new contacts to open chats
                    </Checkbox>
                  </Styled.FormGroup>
                </>
              )}
              <Styled.FormGroup>
                <CustomSelect
                  key={data.lang}
                  name="default-language"
                  title="default-language"
                  value={languageValueOption}
                  options={languageOptions}
                  getOptionValue={option => option.value}
                  getOptionLabel={option => option.label}
                  onChange={option => setData(old => ({ ...old, lang: option.value }))}
                  label="Set primary language"
                />
              </Styled.FormGroup>
              <SimpleButton
                isLoader={mutateSettings.isLoading}
                type="submit"
                isDisabled={isActionButtonDisabled}
                formId="save-settings"
                size="small"
              >
                Save
              </SimpleButton>
            </Styled.Form>
          </Styled.Body>
        )}
        {!isLoadingAll && (
          <Styled.Aside>
            <MessengerWidget appId={appId} supportType={data.supportType} />
          </Styled.Aside>
        )}
      </Styled.Content>
    </Styled.LocalPageHolder>
  );
};

MessengerSettings.propTypes = {
  companyId: PropTypes.number.isRequired,
  parentCompanyId: PropTypes.number.isRequired,

  appId: PropTypes.string.isRequired,
  psaType: PropTypes.string
};

MessengerSettings.defaultProps = {
  psaType: null
};

const mapStateToProps = state => ({
  psaType: state.usersReducer.user.psaType,
  parentCompanyId: state.usersReducer.user.company.id
});

export default connect(mapStateToProps)(MessengerSettings);
